import { useMutation } from "@tanstack/react-query";
import { use } from "react";
import {
  EditCardRequest,
  UniqueCheckResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { useUserUidNumber } from "../auth/useUserUidNumber.ts";
import { useTranslator } from "../i18n/translator.ts";
import { SnackbarContext } from "../snackbar/SnackbarContext.ts";
import { NotUniqueError } from "./errors.ts";
import { useEditCardEditCache } from "./useEditCardEditCache.ts";
import { useEditCardGlobalSearchCache } from "./useEditCardGlobalSearchCache.ts";
import { useEditCardMySignedCardsCache } from "./useEditCardMySignedCardsCache.ts";
import { useEditCardProjectCache } from "./useEditCardProjectCache.ts";
import { useInvalidateCardErrorsCache } from "./useInvalidateCardErrorsCache.ts";

export const useEditCard = () => {
  const translator = useTranslator();
  const { getToken } = useAuthentication();
  const userUidNumber = useUserUidNumber();
  const editCardProjectCache = useEditCardProjectCache();
  const editCardGlobalSearchCache = useEditCardGlobalSearchCache();
  const editCardEditCache = useEditCardEditCache();
  const editCardMySignedCardsCache = useEditCardMySignedCardsCache();
  const invalidateCardErrorsCache = useInvalidateCardErrorsCache();
  const { displayError, displaySuccess } = use(SnackbarContext);
  const { error, isPending, mutate, isError, reset } = useMutation({
    mutationFn: async (newCard: EditCardRequest): Promise<Response> => {
      const token = await getToken();

      if (newCard.recipient.email) {
        const uniqueCheckResponse = await fetch(
          `/api/projects/${encodeURIComponent(newCard.projectId)}/cardUnique?email=${encodeURIComponent(newCard.recipient.email)}&currCardId=${newCard.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!uniqueCheckResponse.ok) {
          throw new Error("Checking the card email failed");
        }

        const { isUnique } = UniqueCheckResponse.parse(
          await uniqueCheckResponse.json(),
        );

        if (!isUnique) {
          throw new NotUniqueError();
        }
      }

      const response = await fetch(
        `/api/projects/${newCard.projectId}/cards/${newCard.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newCard),
        },
      );

      if (!response.ok) {
        throw new Error("Saving the card failed");
      }

      return response;
    },
    onSuccess: (_, newCard: EditCardRequest) => {
      editCardProjectCache(newCard, userUidNumber);
      editCardGlobalSearchCache(newCard);
      editCardEditCache(newCard);
      editCardMySignedCardsCache(newCard);
      invalidateCardErrorsCache();
      displaySuccess(translator("changesSaved"));
    },
    onError: (e) => {
      if (e instanceof NotUniqueError) {
        displayError(translator("errorCardEmailNotUnique"));
      } else {
        displayError(translator("errorSavingChanges"));
      }
    },
  });

  return { mutate, isPending, error, isError, reset };
};
